// ============================================================
// CLUBHUB – Nachrichten: Wer bekommt die Nachricht?
// ============================================================
// EINE Stelle, die aus der Empfaengerauswahl (`AudienceView`) die
// Liste der Menschen macht — fuer den Zaehler im Editor, die
// Vorschau, die Liste und spaeter den Versand selbst. Rein, ohne
// Datenbank: dieselbe Rechnung laeuft im Browser (Live-Zaehler) und
// auf dem Server (Versand).
//
// DIE REGELN, in dieser Reihenfolge:
//   1. Gruppen (mit Untergruppen), eingeschraenkt auf die gewaehlten
//      Status — ohne Gruppe gilt der Status allein.
//   2. Zusaetzliche Personen kommen dazu.
//   3. Von Hand Ausgeschlossene fallen heraus, Verstorbene immer.
//   4. Newsletter nur mit Einwilligung (Werbung, Art. 6 Abs. 1 a
//      DSGVO); Vereinsinformation an alle gewaehlten Mitglieder.
//   5. Der Weg: E-Mail — wer Post wuenscht oder keine Adresse hat,
//      bekommt einen Brief; SMS nur mit Handynummer; Brief nur mit
//      Anschrift.
// ============================================================
import type { ContactStatus, MailingChannel } from "@/generated/prisma/enums";
import type { ContactView, GroupView } from "@/lib/views";
import type { AudienceView, Zustellweg, Zweck } from "./views";

export type Ausschlussgrund =
  | "HAND"
  | "VERSTORBEN"
  | "EINWILLIGUNG"
  | "HANDYNUMMER"
  | "ANSCHRIFT"
  | "UNERREICHBAR";

export const AUSSCHLUSSGRUND: Record<Ausschlussgrund, { label: string; kurz: string }> = {
  HAND: { label: "Von Hand ausgeschlossen", kurz: "ausgeschlossen" },
  VERSTORBEN: { label: "Verstorben", kurz: "verstorben" },
  EINWILLIGUNG: { label: "Ohne Einwilligung in den Newsletter", kurz: "ohne Einwilligung" },
  HANDYNUMMER: { label: "Ohne Handynummer", kurz: "ohne Handynummer" },
  ANSCHRIFT: { label: "Ohne vollständige Anschrift", kurz: "ohne Anschrift" },
  UNERREICHBAR: { label: "Weder Mailadresse noch Anschrift", kurz: "nicht erreichbar" },
};

/** Warum jemand auf dem Brief-Weg landet, obwohl eine Mail geht. */
export type Briefgrund = "POST_GEWUENSCHT" | "OHNE_MAIL";

export const BRIEFGRUND: Record<Briefgrund, string> = {
  POST_GEWUENSCHT: "wünscht Post",
  OHNE_MAIL: "ohne Mailadresse",
};

export type Empfaenger = {
  kontakt: ContactView;
  /** `null` = wird nicht angeschrieben, siehe `grund`. */
  weg: Zustellweg | null;
  grund: Ausschlussgrund | null;
  briefgrund: Briefgrund | null;
  /** Wie die Person in die Auswahl kam. */
  quelle: "GRUPPE" | "STATUS" | "PERSON";
};

export type Aufloesung = {
  alle: Empfaenger[];
  /** Nur die, die angeschrieben werden. */
  erreicht: Empfaenger[];
  ausgeschlossen: Empfaenger[];
  zahlen: {
    gesamt: number;
    email: number;
    brief: number;
    sms: number;
    briefPost: number;
    briefOhneMail: number;
    ausgeschlossen: number;
    nachGrund: Record<Ausschlussgrund, number>;
  };
};

/** Die Status, die als „Mitglied“ zaehlen. */
export const MITGLIEDS_STATUS: ContactStatus[] = ["ACTIVE", "PASSIVE", "HONORARY"];

export const LEERE_AUSWAHL: AudienceView = {
  groups: [],
  statuses: [],
  include: [],
  exclude: [],
  purpose: "INFO",
};

/** Die Gruppe und alle ihre Untergruppen. */
export function mitUntergruppen(ids: string[], gruppen: GroupView[]): Set<string> {
  const ergebnis = new Set(ids);
  let gewachsen = true;
  while (gewachsen) {
    gewachsen = false;
    for (const g of gruppen) {
      if (g.parentId && ergebnis.has(g.parentId) && !ergebnis.has(g.id)) {
        ergebnis.add(g.id);
        gewachsen = true;
      }
    }
  }
  return ergebnis;
}

export function hatAnschrift(c: ContactView): boolean {
  return Boolean(c.street && c.zip && c.city);
}

/** Der Weg fuer eine Person, oder warum es keinen gibt. */
export function wegFuer(
  c: ContactView,
  channel: MailingChannel,
  purpose: Zweck,
): Pick<Empfaenger, "weg" | "grund" | "briefgrund"> {
  if (c.status === "DECEASED") return { weg: null, grund: "VERSTORBEN", briefgrund: null };
  if (purpose === "NEWSLETTER" && !c.consentNewsletter) {
    return { weg: null, grund: "EINWILLIGUNG", briefgrund: null };
  }
  if (channel === "SMS") {
    return c.mobile ? { weg: "SMS", grund: null, briefgrund: null } : { weg: null, grund: "HANDYNUMMER", briefgrund: null };
  }
  if (channel === "LETTER") {
    return hatAnschrift(c) ? { weg: "LETTER", grund: null, briefgrund: null } : { weg: null, grund: "ANSCHRIFT", briefgrund: null };
  }
  // E-Mail: wer Post wuenscht oder keine Adresse hat, bekommt einen Brief.
  if (c.communication === "POST") {
    return hatAnschrift(c)
      ? { weg: "LETTER", grund: null, briefgrund: "POST_GEWUENSCHT" }
      : { weg: null, grund: "ANSCHRIFT", briefgrund: null };
  }
  if (!c.email) {
    return hatAnschrift(c)
      ? { weg: "LETTER", grund: null, briefgrund: "OHNE_MAIL" }
      : { weg: null, grund: "UNERREICHBAR", briefgrund: null };
  }
  return { weg: "EMAIL", grund: null, briefgrund: null };
}

/** Die Kandidaten ohne Ausschluss — wen die Auswahl ueberhaupt meint. */
export function kandidaten(
  audience: AudienceView,
  kontakte: ContactView[],
  gruppen: GroupView[],
): { kontakt: ContactView; quelle: Empfaenger["quelle"] }[] {
  const gruppenIds = mitUntergruppen(audience.groups, gruppen);
  const status = new Set(audience.statuses);
  const gesehen = new Set<string>();
  const liste: { kontakt: ContactView; quelle: Empfaenger["quelle"] }[] = [];

  for (const c of kontakte) {
    const inGruppe = gruppenIds.size > 0 && c.groups.some((g) => gruppenIds.has(g.groupId));
    const passtStatus = status.size === 0 || status.has(c.status);
    const treffer = gruppenIds.size > 0 ? inGruppe && passtStatus : status.size > 0 && status.has(c.status);
    if (treffer) {
      liste.push({ kontakt: c, quelle: gruppenIds.size > 0 ? "GRUPPE" : "STATUS" });
      gesehen.add(c.id);
    }
  }
  for (const id of audience.include) {
    if (gesehen.has(id)) continue;
    const c = kontakte.find((k) => k.id === id);
    if (c) {
      liste.push({ kontakt: c, quelle: "PERSON" });
      gesehen.add(id);
    }
  }
  return liste;
}

export function aufloesen(
  audience: AudienceView,
  channel: MailingChannel,
  kontakte: ContactView[],
  gruppen: GroupView[],
): Aufloesung {
  const raus = new Set(audience.exclude);
  const alle: Empfaenger[] = kandidaten(audience, kontakte, gruppen).map(({ kontakt, quelle }) =>
    raus.has(kontakt.id)
      ? { kontakt, quelle, weg: null, grund: "HAND", briefgrund: null }
      : { kontakt, quelle, ...wegFuer(kontakt, channel, audience.purpose) },
  );
  const erreicht = alle.filter((e) => e.weg !== null);
  const ausgeschlossen = alle.filter((e) => e.weg === null);
  const nachGrund = Object.fromEntries(
    (Object.keys(AUSSCHLUSSGRUND) as Ausschlussgrund[]).map((g) => [g, ausgeschlossen.filter((e) => e.grund === g).length]),
  ) as Record<Ausschlussgrund, number>;

  return {
    alle,
    erreicht,
    ausgeschlossen,
    zahlen: {
      gesamt: erreicht.length,
      email: erreicht.filter((e) => e.weg === "EMAIL").length,
      brief: erreicht.filter((e) => e.weg === "LETTER").length,
      sms: erreicht.filter((e) => e.weg === "SMS").length,
      briefPost: erreicht.filter((e) => e.briefgrund === "POST_GEWUENSCHT").length,
      briefOhneMail: erreicht.filter((e) => e.briefgrund === "OHNE_MAIL").length,
      ausgeschlossen: ausgeschlossen.length,
      nachGrund,
    },
  };
}

// ── Zusammenfassungen in Worten ─────────────────────────────

const STATUS_WORT: Record<ContactStatus, string> = {
  ACTIVE: "aktive",
  PASSIVE: "passive",
  HONORARY: "Ehren-",
  PROSPECT: "Interessenten",
  FORMER: "ausgetretene",
  DECEASED: "verstorbene",
  CONTACT: "Kontakte ohne Mitgliedschaft",
};

function aufzaehlen(teile: string[]): string {
  if (teile.length <= 1) return teile.join("");
  return `${teile.slice(0, -1).join(", ")} und ${teile[teile.length - 1]}`;
}

/** „Aktive und passive Mitglieder“, „Alle Mitglieder“ … */
export function statusText(statuses: ContactStatus[]): string {
  const s = new Set(statuses);
  if (s.size === 3 && MITGLIEDS_STATUS.every((x) => s.has(x))) return "Alle Mitglieder";
  if (s.size === 1 && s.has("HONORARY")) return "Ehrenmitglieder";
  if (s.size === 1 && s.has("PROSPECT")) return "Interessenten";
  if (s.size === 1 && s.has("CONTACT")) return "Kontakte ohne Mitgliedschaft";
  const ordnung: ContactStatus[] = ["ACTIVE", "PASSIVE", "FORMER"];
  const mitglieder = ordnung.filter((x) => s.has(x));
  const rest = statuses.filter((x) => !ordnung.includes(x) && x !== "HONORARY");
  const teile: string[] = [];
  if (mitglieder.length) {
    const satz = `${aufzaehlen(mitglieder.map((x) => STATUS_WORT[x]))} Mitglieder`;
    teile.push(satz.charAt(0).toUpperCase() + satz.slice(1));
  }
  if (s.has("HONORARY")) teile.push("Ehrenmitglieder");
  for (const x of rest) teile.push(STATUS_WORT[x].charAt(0).toUpperCase() + STATUS_WORT[x].slice(1));
  return aufzaehlen(teile);
}

/** Die ganze Auswahl als Satz — fuer Protokoll und Bestaetigung. */
export function auswahlSatz(audience: AudienceView, gruppen: GroupView[], kontakte: ContactView[]): string {
  const namen = audience.groups.map((id) => gruppen.find((g) => g.id === id)?.name).filter(Boolean) as string[];
  const teile: string[] = [];
  if (namen.length && audience.statuses.length) {
    teile.push(`${statusText(audience.statuses)} aus ${aufzaehlen(namen)}`);
  } else if (namen.length) {
    teile.push(`${namen.length === 1 ? "Gruppe" : "Gruppen"} ${aufzaehlen(namen)}`);
  } else if (audience.statuses.length) {
    teile.push(statusText(audience.statuses));
  }
  if (audience.include.length) {
    const p = audience.include.length === 1 ? kontakte.find((k) => k.id === audience.include[0])?.displayName ?? "1 Person" : `${audience.include.length} Personen`;
    teile.push(teile.length ? `dazu ${p}` : p);
  }
  let satz = teile.join(", ") || "Noch niemand gewählt";
  if (audience.exclude.length) satz += `, ohne ${audience.exclude.length === 1 ? "1 Person" : `${audience.exclude.length} Personen`}`;
  return satz;
}

/**
 * Die Kurzform fuer die Liste: „1. Mannschaft + 12 weitere“.
 * Vorn steht der erste Teil der Auswahl, hinten, wie viele
 * Empfaenger darueber hinaus dazukommen.
 */
export function kurzbeschreibung(
  audience: AudienceView,
  empfaengerIds: string[],
  gruppen: GroupView[],
  kontakte: ContactView[],
): { kopf: string; weitere: number } {
  const ids = new Set(empfaengerIds);
  if (audience.groups.length > 0) {
    const erste = gruppen.find((g) => g.id === audience.groups[0]);
    const inErster = mitUntergruppen([audience.groups[0]!], gruppen);
    const drin = kontakte.filter((c) => ids.has(c.id) && c.groups.some((g) => inErster.has(g.groupId))).length;
    return { kopf: erste?.name ?? "Gruppe", weitere: Math.max(0, ids.size - drin) };
  }
  if (audience.statuses.length > 0) {
    const s = new Set(audience.statuses);
    const drin = kontakte.filter((c) => ids.has(c.id) && s.has(c.status)).length;
    return { kopf: statusText(audience.statuses), weitere: Math.max(0, ids.size - drin) };
  }
  if (audience.include.length > 0) {
    const erste = kontakte.find((k) => k.id === audience.include[0]);
    return { kopf: erste?.displayName ?? "1 Person", weitere: Math.max(0, ids.size - 1) };
  }
  return { kopf: "Noch keine Empfänger", weitere: 0 };
}

/** „43 Empfänger · 38 per Mail · 3 per Brief · 2 ohne Einwilligung ausgeschlossen“ */
export function zaehlerSatz(a: Aufloesung): string[] {
  const z = a.zahlen;
  const teile = [`${z.gesamt} ${z.gesamt === 1 ? "Empfänger" : "Empfänger"}`];
  if (z.email) teile.push(`${z.email} per Mail`);
  if (z.sms) teile.push(`${z.sms} per SMS`);
  if (z.brief) teile.push(`${z.brief} per Brief`);
  for (const [grund, n] of Object.entries(z.nachGrund) as [Ausschlussgrund, number][]) {
    if (n) teile.push(`${n} ${AUSSCHLUSSGRUND[grund].kurz}${grund === "HAND" ? "" : " ausgeschlossen"}`);
  }
  return teile;
}
